import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BarChart3, GraduationCap, Users, MessageSquare, Phone, X } from 'lucide-react';

const SignupPage = () => {
  const [promoCode, setPromoCode] = useState("");
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const navigate = useNavigate();

  // Set your valid promo code here
  const VALID_PROMO_CODE = "YC_W25";

  // Handle form submission to check the promo code
  const handleSubmit = (e) => {
    e.preventDefault();
    if (promoCode === VALID_PROMO_CODE) {
      setIsCodeValid(true);
      setShowPromoForm(false); // Close the modal
      navigate('/demo'); // Navigate to demo page
    } else {
      alert("Invalid Promo Code");
    }
  };

  return (
    <>
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-4xl bg-white rounded-lg border border-zinc-200 shadow-sm p-4 md:p-8">
          <div className="flex flex-col md:flex-row md:gap-12">
            {/* Left side - Educational Dashboard Preview */}
            <div className="w-full md:w-1/2 mb-8 md:mb-0">
              <div className="rounded-md space-y-2">
                {/* Analytics Card */}
                <div className="p-3 border border-zinc-100 rounded-md bg-zinc-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-zinc-600">Learning Progress</span>
                    <BarChart3 className="w-3.5 h-3.5 text-zinc-400" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="h-1.5 bg-orange-200 rounded-full w-3/4" />
                    <div className="h-1.5 bg-orange-200 rounded-full w-1/2" />
                  </div>
                </div>

                {/* Students Card */}
                <div className="p-3 border border-zinc-100 rounded-md bg-zinc-50/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-3.5 h-3.5 text-zinc-400" />
                    <span className="text-sm font-medium text-zinc-600">Active Students</span>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-6 h-6 rounded-full bg-zinc-200" />
                    <div className="w-6 h-6 rounded-full bg-zinc-200" />
                    <div className="w-6 h-6 rounded-full bg-zinc-200" />
                    <div className="w-6 h-6 rounded-full bg-zinc-200 flex items-center justify-center text-xs text-zinc-600">+5</div>
                  </div>
                </div>

                {/* Course Card */}
                <div className="p-3 border border-zinc-100 rounded-md bg-zinc-50/50">
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap className="w-3.5 h-3.5 text-zinc-400" />
                    <span className="text-sm font-medium text-zinc-600">Courses</span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="h-2 bg-zinc-200 rounded w-3/4" />
                    <div className="h-2 bg-zinc-200 rounded w-1/2" />
                  </div>
                </div>

                {/* AI Assistant Card */}
                <div className="p-3 border border-zinc-100 rounded-md bg-zinc-50/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <MessageSquare className="w-3.5 h-3.5 text-orange-500" />
                        <span className="text-sm font-medium text-zinc-600">Chat</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5 text-orange-500" />
                        <span className="text-sm font-medium text-zinc-600">Call</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="space-y-1.5 flex-1">
                      <div className="h-1.5 bg-orange-200 rounded-full w-2/3" />
                      <div className="h-1.5 bg-orange-200 rounded-full w-1/2" />
                    </div>
                    <div className="flex gap-1 ml-3">
                      <div className="w-1 h-3 bg-orange-200 rounded-full" />
                      <div className="w-1 h-2 bg-orange-200 rounded-full" />
                      <div className="w-1 h-4 bg-orange-200 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side content */}
            <div className="w-full md:w-1/2 flex flex-col items-center text-center">
              <div className="font-semibold text-2xl tracking-tight mb-6 md:mb-8">
                Moinduel
              </div>
              
              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                <p className="text-sm md:text-base leading-7 text-zinc-600">
                  <span className="text-orange-500 font-semibold bg-orange-100/50 px-1.5 rounded">AI-powered platform</span>
                  {' '}for modern education
                </p>
                
                <p className="text-sm md:text-base leading-7 text-zinc-600">
                  Help students learn{' '}
                  <span className="text-orange-500 font-semibold bg-orange-100/50 px-1.5 rounded">smarter and faster</span>
                </p>
                
                <p className="text-sm md:text-base leading-7 text-zinc-600">
                  Manage your tutoring sessions and payments {' '}
                  <span className="text-orange-500 font-semibold bg-orange-100/50 px-1.5 rounded">easily and flexibly</span>
                  {' '} with AI assistance
                </p>
              </div>

              {/* Buttons */}
              <div className="space-y-4 w-full md:w-56">
                <a 
                  href="https://calendly.com/abdurasulovs01-27/30min?month=2024-11"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-zinc-900 text-white rounded-md py-2 text-sm font-medium hover:bg-zinc-800 transition-colors inline-block text-center"
                >
                  Schedule a Demo
                </a>

                <button
                  onClick={() => setShowPromoForm(true)}
                  className="w-full bg-orange-500 text-white rounded-md py-2 text-sm font-medium hover:bg-orange-600 transition-colors"
                >
                  Enter Promo Code
                </button>
              </div>

              <div className="mt-4 md:mt-6">
                <Link 
                  to="/privacy-policy" 
                  className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Promo Code Modal */}
      {showPromoForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button
              onClick={() => setShowPromoForm(false)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl font-semibold mb-6 text-center">Enter Promo Code</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter your promo code"
                className="w-full px-4 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <button
                type="submit"
                className="w-full bg-orange-500 text-white rounded-md py-2 text-sm font-medium hover:bg-orange-600 transition-colors"
              >
                Submit
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SignupPage;